import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface RadioOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface RadioGroupProps {
  options?: Array<string | number | RadioOption>;
  value?: string | number;
  onChange?: (value: string | number) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

/**
 * RadioGroup - Modern radio button group component
 * Provides single-choice selection with better UX than default radio buttons
 */
const RadioGroup: React.FC<RadioGroupProps> = ({
  options = [],
  value,
  onChange,
  name,
  required = false,
  disabled = false,
  orientation = 'vertical',
  className = '',
  ...props
}) => {
  const { colors } = useTheme();

  const handleChange = (optionValue: string | number) => {
    if (disabled) return;
    onChange?.(optionValue);
  };

  const containerClass = cn(
    orientation === 'horizontal' ? 'flex flex-row gap-4' : 'flex flex-col gap-2',
    className
  );

  return (
    <div className={containerClass} role="radiogroup" {...props}>
      {options.map((option, index) => {
        const optionValue = typeof option === 'string' || typeof option === 'number' ? option : option.value;
        const optionLabel = typeof option === 'string' || typeof option === 'number' ? String(option) : option.label;
        const optionDisabled = disabled || (typeof option === 'object' && option.disabled);
        const isSelected = value === optionValue;
        const radioId = `${name}-${index}`;

        return (
          <label
            key={optionValue}
            htmlFor={radioId}
            className={cn(
              'flex items-center gap-3 cursor-pointer transition-all duration-200',
              optionDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80',
              isSelected ? colors.primaryFg : colors.text
            )}
          >
            <div className="relative">
              <input
                id={radioId}
                type="radio"
                name={name}
                value={optionValue}
                checked={isSelected}
                onChange={() => handleChange(optionValue)}
                required={required}
                disabled={optionDisabled}
                className="sr-only"
              />

              {/* Custom Radio Button */}
              <div
                className={cn(
                  'w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center',
                  isSelected
                    ? cn(colors.primary, colors.primaryBorderAll)
                    : cn(colors.border, colors.surface),
                  !optionDisabled && colors.interactiveHover,
                  optionDisabled
                    ? 'opacity-50'
                    : cn('focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-opacity-50', colors.primaryBorderAll ? `focus-within:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '')
                )}
              >
                {/* Inner Circle */}
                {isSelected && (
                  <div className="w-2 h-2 rounded-full bg-white animate-in zoom-in-50 duration-200" />
                )}
              </div>
            </div>

            {/* Label */}
            <span className={cn('text-sm font-medium', optionDisabled ? 'opacity-50' : '')}>{optionLabel}</span>
          </label>
        );
      })}
    </div>
  );
};

/**
 * RadioGroupItem - Individual radio item (for more granular control)
 */
interface RadioGroupItemProps {
  value: string | number;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const RadioGroupItem: React.FC<RadioGroupItemProps> = ({
  value,
  children,
  disabled = false,
  className = '',
  ...props
}) => {
  const { colors } = useTheme();

  return (
    <div className={cn('flex items-center gap-3', className)} {...props}>
      <div
        className={cn(
          'w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center',
          colors.border,
          colors.surface,
          !disabled && colors.interactiveHover,
          disabled ? 'opacity-50' : cn('focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-opacity-50', colors.primaryBorderAll ? `focus-within:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '')
        )}
      >
        <input type="radio" value={value} disabled={disabled} className="sr-only" {...props} />
      </div>
      {children && (
        <span className={cn('text-sm font-medium', disabled ? 'opacity-50' : colors.text)}>{children}</span>
      )}
    </div>
  );
};

export { RadioGroup, RadioGroupItem };
export default RadioGroup;

